/**
 * @file persistence-coordinator.ts
 * @description Handles saving and restoring tool settings across sessions.
 * #2095: Tool settings persistence.
 */

import { g, Tool } from '@hcie/core';
import { EventBus, loadToolSettings, saveToolSettings, syncStateToSettings, syncSettingsToState, ToolSettings } from '@hcie/shared';

/**
 * Initializes the persistence logic.
 */
export function initPersistenceCoordinator(): void {
    console.log('[Persistence-Coordinator] Initializing tool settings persistence...');

    // 1. Load saved settings and apply immediately to 'g'
    const settings = loadToolSettings();
    syncSettingsToState(settings, g as any);
    
    // Switch to last used tool if possible
    const s = settings as any;
    if (s.lastToolId && (Tool as any)[s.lastToolId]) {
        // We delay this briefly to ensure UI is ready
        setTimeout(() => {
            EventBus.emit('TOOL_CHANGED', { toolId: s.lastToolId });
        }, 100);
    }

    // Notify UI that settings are ready
    EventBus.emit('SETTINGS_LOADED');

    // 2. Setup save listeners
    setupAutoSave();
}

/**
 * Regularly saves relevant g properties to localStorage.
 */
function setupAutoSave(): void {
    let saveTimeout: any;

    const debouncedSave = () => {
        clearTimeout(saveTimeout);
        saveTimeout = setTimeout(() => {
            const currentSettings = loadToolSettings();
            syncStateToSettings(g as any, currentSettings);
            saveToolSettings(currentSettings);
        }, 1000); 
    };

    // Listen to various events that indicate a setting might have changed
    EventBus.on('TOOL_CHANGED', debouncedSave);
    EventBus.on('COLOR_CHANGED', debouncedSave);
    
    // We also hook into property changes via a global window event if available
    window.addEventListener('hcie-setting-updated', debouncedSave);
    window.addEventListener('penWidthChanged', debouncedSave);

    // Watch for slider style changes that might not emit events
    const sliders = ['sizeSlider', 'opacitySlider', 'hardnessSlider'];
    sliders.forEach(id => {
        const el = document.getElementById(id);
        if (el) el.addEventListener('change', debouncedSave);
    });

    // Periodically save as a fallback
    setInterval(() => {
        const currentSettings = loadToolSettings();
        syncStateToSettings(g as any, currentSettings);
        saveToolSettings(currentSettings);
    }, 30000);
}
